/**
 * @file ItemSupplierPicker.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-02
 * @modified 2026-11-02
 * @description An inventory item's Supplier, chosen (Cam, 30 Oct / 2 Nov
 *              2026): "No supplier", each supplier GROUP ("Mole Valley Feed
 *              Solutions group (any depot — pick on the order)" — first, and
 *              found by its depots' names too), and every active supplier
 *              contact in no group (account number as a badge). A group's
 *              members are not offered on their own: an item bought from any
 *              of them IS the group's (utils/itemSupplier — the server stores
 *              it so whatever is sent). The item's current supplier is always
 *              listed, even when archived or not a supplier contact.
 *
 *              Used by the item windows' "Change supplier…" (ItemSupplierRow)
 *              and Edit / Add Item (ItemSupplierField). Values are the keys
 *              'a:<id>' / 'g:<parent id>' / '' (utils/itemSupplier.supplierKey).
 */

import React, { useMemo } from 'react';
import SearchableDropdown from './SearchableDropdown';
import { groupOptionLabel } from '../utils/itemSupplier';

/** The dropdown's options from GET /api/inventory/supplier-options, keeping `current`. */
export function itemSupplierOptions({ suppliers = [], groups = [] } = {}, current = null) {
  const out = [{ value: '', label: 'No supplier' }];
  for (const g of groups) {
    out.push({
      value: `g:${g.parent_id}`,
      label: groupOptionLabel(g.name),
      tag: 'Group',
      pinned: true,
      searchText: `${g.name} group ${(g.members || []).map(m => m.name).join(' ')}`
    });
  }
  for (const s of suppliers) {
    if (s.in_group) continue;
    const key = `a:${s.id}`;
    out.push({
      value: key,
      label: s.name,
      badge: s.account_no || '',
      archived: !!s.archived,
      searchText: `${s.account_no || ''} ${s.name || ''}`
    });
  }
  if (current && current.key && !out.some(o => o.value === current.key)) {
    out.push({ value: current.key, label: current.text || current.name || current.key, archived: !!current.archived });
  }
  return out;
}

function ItemSupplierPicker({ value, onChange, lists, current = null, disabled = false, placeholder = 'Choose the supplier…' }) {
  const options = useMemo(() => itemSupplierOptions(lists || {}, current), [lists, current]);
  return (
    <SearchableDropdown
      options={options}
      value={value || ''}
      onChange={(v) => onChange(v || '')}
      placeholder={placeholder}
      searchPlaceholder="Supplier name, account number or depot"
      disabled={disabled}
    />
  );
}

export default ItemSupplierPicker;
